import React from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { useDispatch } from "react-redux";

import A5Report from "@/containers/Dashboard/Providers/A5Report";
import A5ScreeningDetails from "@/components/Common/A5ScreeningDetails";
import A5ScreeningList from "@/components/Common/A5ScreeningList";
import AddAppointment from "@/components/Provider/ScheduleAvailability/AddAppointment";
import { admin_admin, admin_bp, admin_cse, adminRoute, baseRoute } from "@/config/roles";
import { AdminProfile } from "@/portals/(admin)/(profile)/AdminProfile";
import { AdminProfileOthers } from "@/portals/(admin)/(profile)/AdminProfileOthers";
import { AdvancedCareActivityNewNote } from "@/features/notes/(v2)/(advanced-care)/AdvancedCareActivityNewNote";
import { canCreateAdvancedCareActivityNotes } from "@/features/acp/helpers/note-creation";
import CareTeamList from "@/containers/Dashboard/Providers/PatientProfileView/CareTeamList";
import ChangePassword from "@/components/Authentication/ChangePassword";
import ClinicalAssessment from "@/containers/Dashboard/Providers/PatientProfileView/ClinicalAssessment";
import { CustomerSuccess } from "@/portals/(admin)/(customer-success)/CustomerSuccess";
import type { CustomSection } from "@/components/Navigation/Toolbar/Views/DesktopToolbar";
import DischargeData from "@/components/Provider/PatientView/MemberProfile/DischargeData";
import { DraftNotes } from "@/portals/(shared)/(draft-notes)/DraftNotes";
import { FileList } from "@/portals/(shared)/(file-list)/FileList";
import { Footer } from "@/component-library/Footer";
import GAD7 from "@/features/assessments/GAD7";
import { getAdminProfile } from "@/redux/actions/auth";
import IBARReport from "@/containers/Dashboard/Providers/IBARReport";
import InsuranceEligibilityQueueManagement from "@/components/Admin/InsuranceEligibilityQueueManagement/InsuranceEligibilityQueueManagement";
import InsuranceEligibilityRecordManagement from "@/components/Admin/InsuranceEligibilityRecordManagement";
import IPSRReport from "@/containers/Dashboard/Providers/IPSRReport";
import { isAuthorized } from "@/utils/authToken";
import { Main2 } from "@/components/layout/Main";
import { MemberProfile } from "@/features/member-profile/MemberProfile";
import NewPractice from "@/portals/(admin)/(practice)/NewPractice";
import { NewProvider } from "@/portals/(admin)/(practice)/NewProvider";
import NewReport from "@/containers/Dashboard/Providers/NewReport";
import { NoteDetailPage } from "@/features/notes/(legacy)/NoteDetailPage";
import { NoteViewV2 } from "@/features/notes/(v2)/NoteViewV2";
import OBARReport from "@/containers/Dashboard/Providers/OBARReport";
import OmnisearchList from "@/portals/(admin)/(omnisearch)/OmnisearchList";
import OnBehalfOfSchedule from "@/containers/Dashboard/Providers/OnBehalfOfSchedule";
import OPSRReport from "@/containers/Dashboard/Providers/OPSRReport";
import Patient from "@/containers/Dashboard/Providers/Patient";
import PatientsList from "@/portals/(shared)/(patient-list)/PatientsList";
import PHQ9 from "@/features/assessments/PHQ9";
import Practice from "@/portals/(admin)/(practice)/Practice";
import PracticeList from "@/portals/(admin)/(practice)/PracticeList";
import { PrintNoteView } from "@/features/notes/(v2)/PrintNoteView";
import PrivacyPolicy from "@/components/Common/PrivacyPolicy";
import Provider from "@/portals/(admin)/(practice)/Provider";
import { ProviderProfile } from "@/portals/(provider)/(profile)/ProviderProfile";
import Ratings from "@/containers/Dashboard/Providers/PatientProfileView/Ratings";
import ReferralView from "@/containers/Dashboard/Providers/ReferralView";
import Reports from "@/containers/Dashboard/Providers/Reports";
import SchedulePageV2 from "@/containers/Dashboard/Providers/SchedulePageV2";
import Settings from "@/portals/(shared)/components/SettingsPage";
import { Toolbar } from "@/components/Navigation/Toolbar/Toolbar";
import TutorialScreens from "@/components/Common/TutorialScreen";
import { useCosignSupervisor } from "@/features/cosignature/hooks/useCosignSupervisor";
import { useCurrentUserId } from "@/lib/hooks/useCurrentUserId";
import { useUserPermissions } from "@/features/permissions/api/queries/useUserPermissions";
import { WaitlistForm } from "@/components/Common/WaitlistForm/WaitlistForm";
import WorkingHours from "@/portals/(shared)/components/WorkingHours";

import NewNote from "../(shared)/(new-note)/NewNote";
import ProgressNoteList from "../(shared)/ProgressNoteList";
import { ViewDraftNote } from "../(shared)/(new-note)/DraftNoteView";
import ViewDraftNoteV2 from "../(shared)/(new-note)/DraftNoteViewV2";

import { ADMIN_BASE_REDIRECT_ROUTE } from "./constants";
import {
  ADMIN_OR_CSE_ROUTE_SECTION_OPTIONS,
  BILLING_PARTNER_ROUTE_SECTION_OPTIONS,
  CO_SIGN_NAV_ITEM,
  DEFAULT_ROUTE_SECTION_OPTIONS,
} from "./constants/router";
import { Cosignature } from "./(cosignature)/Cosignature";

export function AdminRouter() {
  const dispatch = useDispatch();
  const { isSupervisor } = useCosignSupervisor();
  const userId = useCurrentUserId();

  useUserPermissions({ userId }); // prefetch permissions

  const isCse = isAuthorized(admin_cse);
  const isAdminOrCse = isCse || isAuthorized(admin_admin);

  const routeSectionOptions = (() => {
    if (isAdminOrCse) {
      if (isSupervisor) {
        const options = [...ADMIN_OR_CSE_ROUTE_SECTION_OPTIONS];

        // insert after Screening (index 1)
        options.splice(2, 0, CO_SIGN_NAV_ITEM);

        return options;
      }

      return ADMIN_OR_CSE_ROUTE_SECTION_OPTIONS;
    }
    if (isAuthorized(admin_bp)) return BILLING_PARTNER_ROUTE_SECTION_OPTIONS;

    return DEFAULT_ROUTE_SECTION_OPTIONS;
  })();

  const navbarConfiguration = {
    customRenderSection: [] as CustomSection[],
    routeSection: { options: routeSectionOptions },
    selfSection: {},
  };

  React.useEffect(() => {
    dispatch(getAdminProfile());
  }, [dispatch]);

  return (
    <>
      <Toolbar
        baseRoute={baseRoute}
        isMessagingEnabled={false}
        navbarConfiguration={navbarConfiguration}
      />

      <Main2>
        <Switch>
          {isCse && (
            <Route
              component={WaitlistForm}
              path={adminRoute + "/patient/:patientId/waitlist_form"}
            />
          )}

          <Route
            path={adminRoute + "/settings"}
            render={() => <Settings baseRoute={adminRoute} />}
          />

          <Route
            path={adminRoute + "/schedule/:providerId/view"}
            render={(props) => <SchedulePageV2 {...props} />}
          />

          <Route component={AddAppointment} path={adminRoute + "/schedule/:providerId/add"} />
          <Route component={OnBehalfOfSchedule} path={adminRoute + "/schedule/:providerId"} />

          <Route
            path={adminRoute + "/profile/hours/:providerId"}
            render={(props) => <WorkingHours {...props} baseRoute={adminRoute} />}
          />

          <Route component={AdminProfileOthers} path={adminRoute + "/profile/admin/:adminId"} />
          <Route path={adminRoute + "/profile/:providerId"} render={() => <ProviderProfile />} />
          <Route component={AdminProfile} path={adminRoute + "/profile"} />
          <Route component={NewReport} path={adminRoute + "/reports/add"} />

          <Route
            path={adminRoute + "/reports"}
            render={(props) => <Reports {...props} baseRoute={adminRoute} />}
          />

          <Route
            path={adminRoute + "/privacy-policy"}
            render={() => <PrivacyPolicy baseRoute={adminRoute} />}
          />

          <Route
            path={adminRoute + "/tutorial"}
            render={() => <TutorialScreens baseRoute={adminRoute} />}
          />

          <Route
            path={adminRoute + "/change-password"}
            render={(props) => <ChangePassword {...props} baseRoute={adminRoute} />}
          />

          <Route component={NewPractice} path={adminRoute + "/practice/add"} />
          <Route component={NewProvider} path={adminRoute + "/practice/:practiceId/provider/add"} />

          <Route
            path={adminRoute + "/practice/:practiceId/provider/:providerId/patientsList"}
            render={(props) => (
              <PatientsList
                practiceId={props.match.params.practiceId}
                providerId={props.match.params.providerId}
                {...props}
                baseRoute={adminRoute}
              />
            )}
          />

          <Route
            path={adminRoute + "/practice/:practiceId/provider/:providerId"}
            render={(props) => <Provider {...props} baseRoute={adminRoute} />}
          />

          <Route
            path={adminRoute + "/practice/:practiceId"}
            render={(props) => <Practice {...props} baseRoute={adminRoute} />}
          />

          <Route
            path={adminRoute + "/practice"}
            render={(props) => <PracticeList {...props} baseRoute={adminRoute} />}
          />

          <Route
            path={adminRoute + "/search"}
            render={(props) => <OmnisearchList {...props} baseRoute={adminRoute} />}
          />

          <Route
            path={adminRoute + "/patient/:patientId/file"}
            render={() => <FileList type="patient" />}
          />

          <Route component={NewNote} path={adminRoute + "/patient/:patientId/note/add"} />
          <Route
            component={ViewDraftNoteV2}
            path={adminRoute + "/patient/:patientId/note/:noteId/v2/draft"}
          />
          <Route
            component={ViewDraftNote}
            path={adminRoute + "/patient/:patientId/note/:noteId/draft"}
          />
          <Route component={PrintNoteView} path={adminRoute + "/patient/:patientId/export_notes"} />
          <Route component={NoteViewV2} path={adminRoute + "/patient/:patientId/note/:noteId/v2"} />
          <Route
            component={NoteDetailPage}
            path={adminRoute + "/patient/:patientId/note/:noteId"}
          />

          <Route component={ProgressNoteList} path={adminRoute + "/patient/:patientId/note"} />

          {canCreateAdvancedCareActivityNotes() && (
            <Route
              component={AdvancedCareActivityNewNote}
              path={adminRoute + "/patient/:patientId/advanced_care_activity"}
            />
          )}

          <Route
            component={ClinicalAssessment}
            path={adminRoute + "/patient/:patientId/clinical_assessment"}
          />

          <Route component={MemberProfile} path={adminRoute + "/patient/:patientId/profile/v2"} />
          <Route component={CareTeamList} path={adminRoute + "/patient/:patientId/care_team"} />
          <Route component={A5Report} path={adminRoute + "/patient/:patientId/report/a5"} />
          <Route component={IBARReport} path={adminRoute + "/patient/:patientId/report/ibar"} />
          <Route component={IPSRReport} path={adminRoute + "/patient/:patientId/report/ipsr"} />
          <Route component={OBARReport} path={adminRoute + "/patient/:patientId/report/obar"} />
          <Route component={OPSRReport} path={adminRoute + "/patient/:patientId/report/opsr"} />
          <Route component={Ratings} path={adminRoute + "/patient/:patientId/ratings"} />
          <Route component={PHQ9} path={adminRoute + "/patient/:patientId/phq-9"} />
          <Route component={GAD7} path={adminRoute + "/patient/:patientId/gad-7"} />
          <Route component={Reports} path={adminRoute + "/patient/:patientId/report"} />

          <Route
            component={DischargeData}
            path={adminRoute + "/patient/:patientId/discharge/:dischargeId"}
          />

          <Route component={Patient} path={adminRoute + "/patient/:patientId"} />

          {isAdminOrCse && (
            <Route component={CustomerSuccess} path={adminRoute + "/customer-success"} />
          )}

          <Route
            path={adminRoute + "/a5_screenings/:screening_id/file"}
            render={() => <FileList type="a5_screenings" />}
          />

          <Route
            component={A5ScreeningDetails}
            path={adminRoute + "/a5_screenings/:screening_id"}
          />

          <Route component={A5ScreeningList} path={adminRoute + "/a5_screenings"} />
          <Route
            path={adminRoute + "/referral/:referralCode/file"}
            render={() => <FileList type="referral" />}
          />

          <Route component={ReferralView} path={adminRoute + "/referral/:referralCode"} />
          <Route component={DraftNotes} path={adminRoute + "/draft-notes/:providerId"} />

          {isAdminOrCse && (
            <Route
              component={InsuranceEligibilityRecordManagement}
              path={adminRoute + "/eligibility/:id"}
            />
          )}

          {isAdminOrCse && (
            <Route
              component={InsuranceEligibilityQueueManagement}
              path={adminRoute + "/eligibility"}
            />
          )}

          <Route component={Cosignature} path={adminRoute + "/cosignature"} />
          <Redirect from={adminRoute} to={ADMIN_BASE_REDIRECT_ROUTE} />
        </Switch>
      </Main2>

      <Footer />
    </>
  );
}
